import { createCustomerUserApi, updateCustomerUserApi, useCustomerUserDetail } from "@/api/services/main/customer-user";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { Form } from "@/components/elements/Form";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { CustomerUserBasicInfoFields } from "@/features/customer-user/components/form/CustomerUserBasicInfoFields";
import { CustomerUserStatusFields } from "@/features/customer-user/components/form/CustomerUserStatusFields";
import { CustomerUserFormFooter } from "@/features/customer-user/components/form/CustomerUserFormFooter";
import { CustomerUserProductAccessFields } from "@/features/customer-user/components/form/CustomerUserProductAccessFields";
import { getPICGuardOffenders, TCannotDeactivateUser, UserCannotDeactivateModal } from "@/features/customer-user/components/UserCannotDeactivateModal";
import { useSelectableProductContracts } from "@/features/customer-user/hooks/useSelectableProductContracts";
import { customerUserFormSchema } from "@/features/customer-user/schema/customerUserFormSchema";
import { TCustomerUserForm } from "@/features/customer-user/types/customerUserForm";
import { accessibleProductCodes } from "@/features/customer-user/utils/productAccess";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AppRoutes, customerUserPath } from "@/routes/config";
import { text } from "@/theme/colors";
import { layout } from "@/theme/variables";
import { USER_ROLE, USER_STATUS, TUserRole } from "@/types/enum";
import { isApiError, setErrorForm, showError } from "@/utils/error";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { INVALID_ALREADY_EXISTS } from "@/constants/invalids";
import { replaceStr } from "@/utils/string";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Stack, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useVisible } from "@/hooks/useVisible";
import { Navigate, useNavigate, useParams } from "react-router-dom";

type TCustomerUserFormPageProps = { mode: "create" | "edit" };

const PAGE_COPY = {
  create: { title: "ユーザー追加", subtitle: "ユーザーを追加できます。" },
  edit: { title: "ユーザー編集", subtitle: "ユーザー情報を編集できます。" },
} as const;

const createDefaults: TCustomerUserForm = { fullName: "", email: "", newEmail: null, role: USER_ROLE.MEMBER, status: USER_STATUS.ACTIVE, productCodes: [] };

export const CustomerUserFormPage: FC<TCustomerUserFormPageProps> = ({ mode }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const copy = PAGE_COPY[mode];

  const { data: user, isLoading } = useCustomerUserDetail(isEdit ? id : undefined);
  // Only the emptiness matters here: it tells whether the org holds any contract the user could be
  // granted — i.e. whether the selection is mandatory. Resolved before the form mounts so the
  // resolver context is stable from the first validation.
  const { hasSelectableProductContracts, isLoading: isSelectableLoading } = useSelectableProductContracts();
  const cannotDeactivateModal = useVisible();
  const [usersCannotDeactivate, setUsersCannotDeactivate] = useState<TCannotDeactivateUser[]>([]);
  const [errorMessage, setErrorMessage] = useState("");

  const methods = useForm<TCustomerUserForm>({
    mode: "onChange",
    resolver: yupResolver(customerUserFormSchema),
    // The org holds at least one selectable contract → picking a product becomes mandatory.
    context: { hasSelectableProductContracts },
    defaultValues: createDefaults,
    values: isEdit && user
      ? {
          fullName: user.fullName,
          email: user.email,
          newEmail: user.newEmail ?? null,
          role: user.role || "",
          status: user.status || USER_STATUS.ACTIVE,
          productCodes: accessibleProductCodes(user.productAccess),
        }
      : undefined,
  });
  const { control } = methods;

  const onSubmit = async (data: TCustomerUserForm) => {
    const role = data.role as TUserRole;
    const productCodes = data.productCodes;
    setErrorMessage("");
    try {
      if (isEdit && id) {
        const newEmail = data.newEmail?.trim() || null;
        await updateCustomerUserApi(id, { fullName: data.fullName, newEmail, role, status: data.status, productCodes });
        // status can flip active<->disabled, so refresh the list and the active count alongside the detail.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_DETAIL, id] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_LIST] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_COUNT] }),
        ]);
        toast.success("ユーザー情報を更新しました。");
        methods.reset(data);
        navigate(customerUserPath.detail(id));
      } else {
        await createCustomerUserApi({ fullName: data.fullName, email: data.email, role, productCodes });
        // A newly created user is active, so bump the active count as well as the list.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_LIST] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_COUNT] }),
        ]);
        toast.success("ユーザーを登録しました。登録メールを送信しました。");
        methods.reset(data);
        navigate(AppRoutes.customerUsers);
      }
    } catch (error) {
      if (isApiError(error)) {
        setErrorMessage(error.message);
      }
      // Blocked because the user is set as an org's 担当者 — show the offenders in a modal.
      const offenders = getPICGuardOffenders(error);
      if (offenders) {
        setUsersCannotDeactivate(offenders);
        cannotDeactivateModal.open();
        return;
      }
      if (isApiError(error) && error.code === ERROR_STATUS_CODES.EMAIL_HAS_BEEN_ALREADY_EXISTS) {
        methods.setError(isEdit ? "newEmail" : "email", { message: replaceStr(INVALID_ALREADY_EXISTS, { path: "メールアドレス" }) });
      } else if (isApiError(error) && error.code === ERROR_STATUS_CODES.FULLNAME_HAS_BEEN_ALREADY_EXISTS) {
        methods.setError("fullName", { message: replaceStr(INVALID_ALREADY_EXISTS, { path: "ユーザー名" }) });
      } else {
        showError(error);
      }
      setErrorForm(methods, error);
    }
  };

  if ((isEdit && isLoading) || isSelectableLoading) return <LoadingArea height={400} />;
  if (isEdit && !user) return <Navigate to={AppRoutes.pageNotFound} replace />;

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={customerUserFormSchema}>
      <Stack gap="24px" minHeight="calc(100vh - 105px)">
        <Stack width="100%" alignItems="center">
          <Stack width={layout.pageContentWidth} maxWidth="100%" gap="4px">
            <Typography variant="head20Bold" color={text.primary}>
              {copy.title}
            </Typography>
            <Typography variant="body14" color={text.secondary}>
              {copy.subtitle}
            </Typography>
          </Stack>
        </Stack>

        <Stack flex={1} width="100%" alignItems="center">
          <Box width={layout.pageContentWidth} maxWidth="100%">
            <MenuNavigate
              menuPrefix={
                <EllipsisText
                  title={isEdit ? (user?.fullName ?? "") : copy.title}
                  variant="body14Bold"
                  color={text.primary}
                  maxWidth={168}
                  sx={{ px: "16px", py: "8px" }}
                />
              }
              contents={[
                { title: "ユーザーの状態", content: <CustomerUserStatusFields control={control} user={isEdit ? user : undefined} /> },
                { title: "基本情報", content: <CustomerUserBasicInfoFields control={control} isEdit={isEdit} currentEmail={isEdit ? user?.email : undefined} /> },
                { title: "プロダクトアクセス権限", content: <CustomerUserProductAccessFields control={control} user={isEdit ? user : undefined} /> },
              ]}
              contentAffix={<CustomerUserFormFooter user={isEdit ? user : undefined} />}
            />
          </Box>
        </Stack>
      </Stack>

      <UserCannotDeactivateModal
        isOpen={cannotDeactivateModal.visible}
        onClose={cannotDeactivateModal.close}
        users={usersCannotDeactivate}
        errorMessage={errorMessage}
      />
    </Form>
  );
};
